import { CheckOutlined } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as Sentry from "@sentry/react";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";

const Lottie = React.lazy(() => import('lottie-react'));
import SentryWrap from 'components/Sentry';
import { useDispatch } from "react-redux";
import { useReactToPrint } from "react-to-print";

import { RetweetOutlined } from '@ant-design/icons';
import { Cancel, Print } from '@mui/icons-material';
import { Button as AntdButton, Select as AntSelect, Skeleton, Space, Tag } from "antd";
import SuperLessonEditor from "components/ebook/Superlession/LessionEditor";
import Loading from "components/Loading";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { useMobileDetect, useTabletDetection } from "containers/chat/hooks";
import { filter, find, get, map } from "lodash";
import { useDifferentiationLesson } from "store/DifferentiationLesson/selectors";
import { handleCreateDifferentiationLesson, handleDeleteDifferentiationLesson } from "store/DifferentiationLesson/thunks";
import { updateUI } from "store/UI/actions";
import { useAllDistrictTeacher, useCurrentUser, useUserProfileConfig } from "store/users/selectors";
import styled, { keyframes } from "styled-components";
import { ELEPHANT } from "utils/assets/images/lootie";
import color from "utils/constants/color";
import { cleanHTMLString, converMarkToHtmlText } from "utils/methods/textMethod";
import differentSvg from "../../utils/assets/images/different.svg";
import sysConf from "config";
import { getAwsAuthToken } from 'utils/lib/authHelper';
import AIUsageInfo from 'components/Modals/AiInfoModal';
import usePopUpAtOnce from 'components/hooks/usePopUpAtOnce';
import moment from 'moment-timezone';

const LessonContainer = styled.section`
  padding-top: 15px;
  padding-left: 27px;
  padding-right: 33px;
  padding-bottom: 20px;
  background: transparent;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 50;
  @media (max-width: 700px) {
    display: ${p => p.isCanvasUser ? 'unset' : 'none'};
  }
`;

const LessonModal = styled.div`
  background: #000f4c;
  border-radius: 11px;
  padding-left: ${(p) => (p.view ? "51px" : "19px")};
  padding-right: ${(p) => (p.view ? "51px" : "19px")};
  padding-bottom: ${(p) => (p.view ? "34px" : "26px")};
  padding-top: ${(p) => (p.view ? "51px" : "10px")};
  height: ${(p) => (p.view ? "unset" : "100%")};
  // overflow: hidden auto;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  flex-direction: row;
  overflow:auto;
`;

const SuperLessonLogoImg = styled.img`
  @media (max-width: 768px) {
    width: 57px;
    height: 51px;
  }
`;

const BoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.label`
  color: #fff;
  // font-family: "Gotham";
  font-size: 15px;
  font-style: italic;
  font-weight: 400;
  // font-family: Gotham;
  white-space: nowrap;

  @media (max-width: 768px) {
    color: #fff;
    font-size: 13px;
    font-style: italic;
    font-weight: 400;
  }
`;

const FieldShareBox = styled.div`
  display: grid;
  place-items: center;
  border-radius: 9.5px;
  border: 1px solid #e8e8e8;
  background: #fff;
  text-align: center;
  min-width: 63px;

  // width: 108px;
  height: 31px;
  color: #000;
  // font-family: Gotham;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  flex: 1;
  max-width: 241px;

  @media (max-width: 768px) {
    color: #000;
    // font-family: "Gotham";
    font-size: 10px;
    font-style: italic;
    font-weight: 400;
  }
`;

const WhiteBox = styled.div`
  height: calc(100% - 185px);
  padding: 10px;
  border-radius: 10px;
  border: 2px solid #e8e8e8;
  background: #fff;
  width: 100%;
  // height: 100%;
  // max-height: 700px;

  margin-top: 24px;
  overflow-x: hidden;
  background-color: #fff;
  overflow-y: auto;

  @media (max-width: 1367px) {
    height: calc(100% - 195px);
  }

  @media (max-width: 768px) {
    height: calc(100% - 188px);
  }
  @media print {
    overflow: visible;
    all: unset; /* <-- reset all styles */
    display: block;
    width: 100%;
    height: auto;
    page-break-inside: avoid;
    break-inside: avoid;
    padding: 0;
    margin: 0;
    border: none;
    @page {
      margin-top: 20px;
      margin-bottom: 20px;
      margin-left: 25px;
      margin-right: 25px;
   }
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

const RetryButton = styled.button`
  background-color: #4caf50;
  color: white;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 24px;
  border: none;
  border-radius: 30px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.2s ease;
  animation: ${pulse} 2s infinite;

  &:hover {
    background-color: #43a047;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.97);
  }
`;


const NoMessageContainer = styled.div`
  display:flex;
  align-items:center;
  justify-content:center;
  flex-direction:column;
`;

const useStyles = makeStyles({
  select: {
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent !important",
    },
    menuItemText: {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: "150px",
      display: "block",
    },
    disabledButton: {
      color: "white",
      "&.Mui-disabled": {
        color: "white !important",
      },
    },
  },
});

const DifferentiationViewModal = ({
  open,
  onClose,
  showModal,
  viewLessonType,
  superLessonData,
  data,
  from,
  isCanvasUser,
  bookId,
  pageId,
  viewStreamLesson,
  setViewStreamLesson,
  isStream,
  superLessonConfig = {}
}) => {
  const showAlert = useAlert();
  const imageRef = useRef();
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const superLesson = useDifferentiationLesson();
  const allDistrictTeacher = useAllDistrictTeacher();
  const [loader, setLoader] = useState(false);
  const [loaderRetry, setLoaderRetry] = useState(false);
  const [showShareList, setShowShareList] = useState(true);
  const [printLoader, setPrintloader] = useState(false)
  const filterLesson =
    find(
      superLesson,
      (data) =>
        data?.userId === currentUser?.id &&
        data?.access_by?.includes(currentUser?.id)
    ) || {};
  const filterShareLesson =
    filter(
      superLesson,
      (data) =>
        data?.userId !== currentUser?.id &&
        data?.access_by?.includes(currentUser?.id)
    ) || [];
  const [filterMyData, setFilterMyData] = useState(filterLesson);
  const [filterSharedLesson, setFilterSharedLesson] =
    useState(filterShareLesson);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [initialValues, setInitialValues] = useState([]);
  const superData = filterMyData?.differentiation_data ?? {};
  const filterByTeacher = allDistrictTeacher.find((item) =>
    filterMyData.userId === item.id
  );
  const isTablate = useTabletDetection();
  const isMobile = useMobileDetect() && !isTablate;
  const getAllOptions = useMemo(() => {
    return allDistrictTeacher.map((item) => ({
      label: `${item.firstName} ${item.lastName},`,
      value: `${item?.id}`,
    }));
  }, [allDistrictTeacher]);
  const [alertSakeBar, setAlertSakeBar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const {updatePopUpAtOnce} = usePopUpAtOnce();
  const { isAiPermissionEnabled } = useUserProfileConfig();

  async function streamData(inputData, onChunk) {
    try {
      const token = await getAwsAuthToken();
      const response = await fetch(`${sysConf.apiGateway.AI_BASE_NODE_URL}/DifferentiationStream/differentiation-stream`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          "x-auth-source": sysConf.isDev ? "test" : "prod",
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(inputData),
      });

      if (!response.ok || !response.body) {
        throw new Error('Stream failed to start');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let fullTextAccumulator = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n').filter(Boolean);

        for (const line of lines) {
          if (line.startsWith('data:') && line.replace('data: ', '').trim() === '[DONE]') {
            setLoaderRetry(false);
            if (typeof setViewStreamLesson === 'function') setViewStreamLesson('');
            setAlertSakeBar({ open: true, message: 'Created Successfully', severity: 'success' });
            return; // Exit the function
          }
          fullTextAccumulator += line.replace('data: ', '');
          onChunk(cleanHTMLString(fullTextAccumulator));
        }
      }
    } catch (error) {
      console.error('Stream error:', error);
      setAlertSakeBar({
        open: true,
        message: 'Failed to create lesson. Please try again.',
        severity: 'error',
      });
    }
  }
  const onLessonAi = async (differentiation_data) => {
    if (!isAiPermissionEnabled) {
      setAlertSakeBar({
        open: true,
        message: 'Please enable AI permission to generate content using AI.',
        severity: 'error',
      });
      return;
    }
    const { limit, ...superLessonPayload } = filterMyData;
    const data = {
      ...superLessonPayload,
      bookId,
      pageId,
      from_page: superLessonData.from,
      to_page: superLessonData.to,
      userId: currentUser.id,
      differentiation_data,
      access_by: [currentUser.id],
      mode: import.meta.env.VITE_APP_STAGE?.trim() === "prod" ? "prod" : "dev",
    };
    setLoaderRetry(true);
    const SuperLessonData = {};
    let fullHTML = "";
  
    await streamData(data, async (chunk) => {
      fullHTML += chunk;
        setLoaderRetry(false);
    });
  };



  const sharedProps = {
    mode: "multiple",
    style: {
      width: "100%",
    },
    placeholder: "Select Item...",
    maxTagCount: "responsive",
    showSearch: true,
    optionFilterProp: "label",
    filterOption: (input, option) => {
      const label = (option?.label || '').trim().toLowerCase();
      const searchText = input.trim().toLowerCase();
      if (!searchText) return true;
      const searchWords = searchText.split(/\s+/).filter(word => word.length > 0);
      return searchWords.every(word => label.includes(word));
    },
  };

  const [value, setValue] = useState([]);
  const selectProps = {
    value,
    onChange: (selectedIds) => {
      const newSelections = selectedIds.filter(
        (item) => !initialValues.includes(item.value)
      );

      // Combine the new selections with the initial values
      const updatedSelection = [
        ...value.filter((item) => initialValues.includes(item.value)),
        ...newSelections,
      ];

      setValue(updatedSelection);
    },
    labelInValue: true,
  };

  useEffect(() => {
    setFilterSharedLesson(filterShareLesson);
    setFilterMyData(filterLesson);
    setSelectedTeacherIds(filterLesson?.access_by || []);
    const initialAccessBy =
      filterLesson?.access_by?.filter(
        (element) => element !== filterLesson?.userId
      ) || [];
    setInitialValues(initialAccessBy);
    setValue(
      (
        filterLesson?.access_by?.filter(
          (element) => element !== filterLesson?.userId
        ) || []
      ).map((item) => ({ value: item }))
    );
  }, [superLesson]);

  const prevShowModalRef = useRef(showModal);
  useEffect(() => {
    if (prevShowModalRef.current && !showModal) {
      onClose();
    }
    prevShowModalRef.current = showModal;
  }, [showModal]);

  const isDisable=()=>{
    const selectedTeacher = value?.map((element) => element?.value)
      const combinedArray = [...selectedTeacher];
      combinedArray.push(filterLesson?.userId);
      const shareableArr = filter(combinedArray, (val) => val !== currentUser?.id)
      return shareableArr.length==0 ? false : true;
  }

  const handleShare = async () => {
    try {
      setLoader(true);
      const selectedTeacher = value?.map((element) => element?.value);
      const combinedArray = [...selectedTeacher];
      combinedArray.push(filterLesson?.userId);
      const shareableArr = filter(combinedArray, (val) => val !== currentUser?.id)
      if (shareableArr?.length === 0) return;
      const data = {
        ...filterMyData,
        access_by: combinedArray,
        shared_time: moment().toISOString()
      };
      await dispatch(handleCreateDifferentiationLesson(data));
    } catch (error) {
      Sentry.captureException(`329 Error in handleShare: ${error}`);
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const handleBack = () => {
    setShowShareList(true);
    setFilterSharedLesson(filterShareLesson);
    setFilterMyData(filterLesson);
    onClose();
  };

  const handleShareDelete = async () => {
    const data = {
      ...filterMyData,
      access_by: filter(
        filterMyData?.access_by,
        (access) => access !== currentUser?.id
      ),
    };
    if (data?.access_by?.length === 0) {
      setLoader(true);
      await dispatch(handleDeleteDifferentiationLesson(data?.id));
      setLoader(false);
    } else {
      setLoader(true);
      await dispatch(handleCreateDifferentiationLesson(data));
      setLoader(false);
    }
    setSelectedTeacherIds([]);
    setValue([]);
    setShowShareList(true);
    setFilterSharedLesson(filterShareLesson);
    setFilterMyData(filterLesson);
    onClose();
  };

  const validationPrint =()=>{
    if (isMobile || isTablate) {
      showAlert("Print is not supported on this device!","error");
      return;
  }
    else{
    printCall()
  }
  }
  const printCall= useReactToPrint({
    contentRef: imageRef,
    onBeforeGetContent: async () => { setPrintloader(true) },
    onBeforePrint: async () => {
      setPrintloader(false);
    }
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);  // State to manage dropdown visibility
  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
  
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
  
    const isInitiallySelected = initialValues.includes(value);
  
    const selectedCount = Array.isArray(value) ? value.length : 0;
  
    if (selectedCount > 1) {
      return (
        <Tag
          color={"blue"}
          onMouseDown={onPreventMouseDown}
          closable={!isInitiallySelected && closable}
          onClose={onClose}
          style={{ marginInlineEnd: 4 }}
        >
          more..
        </Tag>
      );
    }
  
    // Handling labelText (only show names)
    let labelText = '';
  
    // Check if label is an array of React elements
    if (Array.isArray(label)) {
      for (let i = 0; i < label.length; i++) {
        const children = label[i]?.props?.children;
        if (children && children !== false && children !== "Shared") { // Exclude "Shared"
          labelText = children;
          break;
        }
      }
    }
    // If label is a string, assign directly
    else if (typeof label === 'string') {
      labelText = label;
    }
    // If label is an object, extract text or name
    else if (typeof label === 'object' && label !== null) {
      labelText = label?.text || label?.name || label?.label || '';
    }
  
    if (!labelText) {
      labelText = 'No Label';
    }
  
    // Only append "..." if the label's length exceeds 15 characters
    const displayedLabel = labelText.length > 15 ? `${labelText.slice(0, 15)}...` : labelText;
  
    // Handle the click to toggle dropdown visibility
    const handleTagClick = (event) => {
      onPreventMouseDown(event);
      setIsDropdownOpen(!isDropdownOpen);  // Toggle dropdown visibility
    };
  
    return (
      <Tag
        color={"blue"}
        onClick={handleTagClick}  // Handle the click to open the dropdown
        closable={!isInitiallySelected && closable}
        onClose={onClose}
        className="selectTagVal"
      >
        {displayedLabel}
      </Tag>
    );
  };

  const getMaskIdFrom = get(
    find(get(data, "content"), (k) => Number(k.pageId) === Number(superLessonData?.from)),
    "numberMasked",
    superLessonData?.from || superLessonConfig?.from
  );

  const getMaskIdTo = get(
    find(get(data, "content"), (k) => Number(k.pageId) === Number(superLessonData?.to)),
    "numberMasked",
    superLessonData?.to || superLessonConfig?.to
  );
  

  const closeAlert = () => {
    setAlertSakeBar({ open: false, message: "", severity: "success" });
  };


  const { Option } = AntSelect;
  return (
    <SentryWrap>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertSakeBar.open}
        autoHideDuration={3000}
        onClose={closeAlert}
      >
        <Alert
          onClose={() => {
            setAlertSakeBar({ open: false, message: '' });
          }}
          severity={alertSakeBar.severity}
        >
          <strong>ALERT:</strong> {alertSakeBar.message}
        </Alert>
      </Snackbar>
      {open && (
        <LessonContainer isCanvasUser={isCanvasUser} className={open ? "differentiation-modal-container" : ""}>
          <LessonModal
            style={{ position: 'relative' }}
            view={viewLessonType === 'shared' && showShareList ? true : false}
          >
            <span
              onClick={async () => {
                updatePopUpAtOnce(false)
                // setSelectedTeacherIds([]);
                setShowShareList(true);
                setFilterSharedLesson(filterShareLesson);
                setFilterMyData(filterLesson);
                await dispatch(updateUI({ openDiffModal: true }));
                onClose();
              }}
            >
              <Cancel
                style={{
                  float: 'right',
                  fontSize: viewLessonType === 'shared' && showShareList ? 25 : 20,
                  color: 'red',
                  background: 'white',
                  borderRadius: 20,
                  cursor: 'pointer',
                  marginBottom: 5,
                }}
              />
            </span>
            <div style={{ float: 'right', marginTop: '-5px', marginRight: '5px' }}>
              <AIUsageInfo />
            </div>
            {viewLessonType === 'shared' && showShareList ? (
              <div className="shared-lesson-container">
                <div className="shared-lesson">Shared Differentiation</div>
                <div className="shared-lesson-title-container">
                  {map(filterSharedLesson, (item) => {
                    const findShareBy = allDistrictTeacher.find((obj) => item.userId === obj?.id);
                    return (
                      <>
                        <div
                          className="share-by-lesson"
                          onClick={() => {
                            setShowShareList(false);
                            setFilterMyData(item);
                          }}
                        >
                          <span className="title-for-share">
                            {`${from !== 'ebook' ? 'Differentiation' : `Differentiation (P. ${getMaskIdFrom}-${getMaskIdTo})`}`}{' '}
                            -
                          </span>
                          <span className="shared-by-text">&nbsp; shared by:</span>
                          <span className="shared-by-teacher-name">{` ${findShareBy?.firstName} ${findShareBy?.lastName}`}</span>
                        </div>
                        <div className="share-by-line-lesson" />
                      </>
                    );
                  })}
                </div>
                <div
                  className="share-by-Back-lesson"
                  onClick={() => {
                    handleBack();
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <AntdButton
                      className="back-button-super-lesson"
                      type="primary"
                      loading={loader}
                      style={{
                        borderRadius: 10,
                        minWidth: 179,
                        backgroundColor: '#017216',
                        border: '1px solid white',
                        minHeight: 55,
                        fontSize: 22,
                      }}
                    >
                      {'Back'}
                    </AntdButton>
                  </span>
                </div>
              </div>
            ) : (
              <>
                {isStream ? (
                  <div className='cstmSkeleton cstmSkeletonSpcTp'><Skeleton active paragraph={{ rows: 3 }} title={false} /></div>
                ) : (
                  <Container>
                    <div className="mainDifferAddon" style={{ overflow: 'auto' }}>
                      <div className="mainDifferentiationLession">
                        <img
                          src={differentSvg}
                          alt=""
                          title=""
                        />
                        <div className="differentiationLessionHead">
                          <span>Differentiation: Basic - Intermediate - Advance</span>
                        </div>
                      </div>

                      {filterMyData?.userId === currentUser?.id ? (
                        <BoxContainer className="shareWithUpr shareWithUprDifer shareWithUprDiferCstm">
                          <div className="shareWithLft">
                            <Label>Share With:</Label>
                            <FieldShareBox
                              style={{
                                background: 'none',
                                flex: '1',
                                border: 'none',
                                placeItems: 'start',
                              }}
                            >
                              <Space
                                direction="vertical"
                                style={{ width: '100%', maxWidth: '100%' }}
                              >
                                <AntSelect
                                  dropdownStyle={{ minWidth: '250px' }}
                                  tagRender={tagRender}
                                  className="dropdown-select setValCstm"
                                  {...sharedProps}
                                  {...selectProps}
                                  menuItemSelectedIcon={null}
                                  maxTagCount={1}
                                >
                                  {getAllOptions.map((item) => {
                                    const cleanLabel = item.label.replace(/,\s*$/, '');
                                    return (
                                      <Option key={item.value} value={item.value} label={cleanLabel}>
                                        {initialValues.includes(item?.value) ? (
                                          <span className="shared-text-super-lesson">Shared</span>
                                        ) : (
                                          <>
                                            {value.some((data) => data.value === item?.value) && (
                                              <CheckOutlined className="checkmark-icon-super-lesson" />
                                            )}
                                          </>
                                        )}
                                        <Space>
                                          {cleanLabel.length > 19
                                            ? cleanLabel.slice(0, 19) + '...'
                                            : cleanLabel}
                                        </Space>
                                      </Option>
                                    );
                                  })}
                                </AntSelect>
                              </Space>
                            </FieldShareBox>
                          </div>

                          <div className="shareWithRght">
                            <div
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                // if (!AssignmentShareList?.includes(render?.id)) {
                                //   handleSend(render?.id)
                                // }
                                if (isDisable()) {
                                  handleShare();
                                }
                              }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                }}
                                className="shareBtnCstm"
                              >
                                <AntdButton
                                  className="share-button-super-lesson"
                                  type="primary"
                                  loading={loader}
                                  disabled={!isDisable()}
                                  style={{
                                    borderRadius: 10,
                                    border: '0px',
                                    color: 'black',
                                    cursor: isDisable() ? 'pointer' : 'not-allowed',
                                    backgroundColor: isDisable() ? undefined : '#b3b3b3',
                                  }}
                                >
                                  Share
                                </AntdButton>
                              </span>
                            </div>

                            <div
                              style={{ cursor: 'pointer', width: '50px' }}
                              onClick={() => {
                                validationPrint();
                              }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                }}
                              >
                                <AntdButton
                                  className="share-button-super-lesson"
                                  type="primary"
                                  style={{
                                    borderRadius: 10,
                                    border: '0px',
                                    color: 'black',
                                  }}
                                >
                                  {printLoader ? <Loading size={20} /> : <Print />}
                                </AntdButton>
                              </span>
                            </div>
                            
                            <BoxContainer className="flex-1">
                              <SuperLessonEditor
                                initialMarkdown={filterMyData?.lesson_content || ''}
                                onSave={() => {}}
                                id={filterMyData?.id}
                                isDifferentaition={true}
                              />
                            </BoxContainer>
                          </div>
                        </BoxContainer>
                      ) : (
                        <BoxContainer
                          style={{
                            marginRight: 6,
                            marginLeft: 108,
                            marginBottom: 22,
                          }}
                        >
                          <Label>Share By:</Label>
                          <FieldShareBox>
                            {`${filterByTeacher?.firstName} ${filterByTeacher?.lastName}`}
                          </FieldShareBox>
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              handleShareDelete();
                            }}
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                              }}
                            >
                              <AntdButton
                                type="primary"
                                loading={loader}
                                style={{
                                  borderRadius: 10,
                                  minWidth: 108,
                                  backgroundColor: color.greentrue,
                                  border: '0px',
                                }}
                              >
                                {'Delete'}
                              </AntdButton>
                            </span>
                          </div>
                          <div
                            style={{ cursor: 'pointer', width: '50px' }}
                            onClick={() => {
                              validationPrint();
                            }}
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                              }}
                            >
                              <AntdButton
                                className="share-button-super-lesson"
                                type="primary"
                                style={{
                                  borderRadius: 10,
                                  border: '0px',
                                  color: 'black',
                                }}
                              >
                                {printLoader ? <Loading size={20} /> : <Print />}
                              </AntdButton>
                            </span>
                          </div>
                        </BoxContainer>
                      )}
                    </div>
                  </Container>
                )}
                <WhiteBox ref={imageRef} className="printable">
                  {isStream || filterMyData?.lesson_content?.trim() ? (
                    <div
                      style={{ maxHeight: 'unset', padding: 0 }}
                      className="lesson-plan-container printable lessonPlanUprCstm"
                      dangerouslySetInnerHTML={{
                        __html: converMarkToHtmlText(
                          isStream ? viewStreamLesson : filterMyData.lesson_content,
                        ),
                      }}
                    />
                  ) : null}
                  {(filterMyData?.lesson_content === null || filterMyData?.lesson_content?.trim()?.length === 0) && (
                     <NoMessageContainer>
                     <div style={{ width: '50%' }}>
                       <Suspense fallback={null}>
                         <Lottie animationData={ELEPHANT.poster} />
                       </Suspense>
                     </div>
                     <div
                       style={{
                         padding: '10px 20px 20px 20px',
                         textAlign: 'center',
                         color: '#ff4d4f',
                         fontWeight: 'bold',
                       }}
                     >
                       Uh-oh! Looks like the internet took a quick break. Give it another shot!
                     </div>
                     <RetryButton disabled={loaderRetry} onClick={() => onLessonAi()}>
                       {loaderRetry ? (
                         <Loading size={16} white />
                       ) : (
                         <span>
                           <RetweetOutlined style={{ marginRight: 5 }} /> Try again
                         </span>
                       )}
                     </RetryButton>
                   </NoMessageContainer>
                  )}
                </WhiteBox>
              </>
            )}
          </LessonModal>
        </LessonContainer>
      )}
    </SentryWrap>
  );
};

export default DifferentiationViewModal;